import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { LucideIcon } from "lucide-react-native";
import {
  Stethoscope,
  CalendarClock,
  FlaskConical,
  ScanHeart,
  Flame,
  BrainCircuit,
  Hourglass,
  Plus,
  SearchX,
  Droplets,
  Activity,
  Dna,
} from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { useInfertility } from "../InfertilityContext";
import type { InfertilityStackParamList } from "../InfertilityNavigator";
import { InfertilityCauseType } from "@louise/medical-history/basic-info/types";

type CauseOption = InfertilityCauseType;
type CauseSex = "AFAB" | "AMAB" | "both";

const causes: {
  id: CauseOption;
  labelKey: string;
  icon: LucideIcon;
  sex: CauseSex;
}[] = [
  // AFAB-only
  {
    id: "tubal_causes",
    labelKey: "med_history.fertility.infertility.cause.tubal_causes",
    icon: Stethoscope,
    sex: "AFAB",
  },
  {
    id: "ovulation_disorders",
    labelKey: "med_history.fertility.infertility.cause.ovulation_disorders",
    icon: CalendarClock,
    sex: "AFAB",
  },
  {
    id: "uterus_anomalies",
    labelKey: "med_history.fertility.infertility.cause.uterus_anomalies",
    icon: ScanHeart,
    sex: "AFAB",
  },
  {
    id: "endometriosis",
    labelKey: "med_history.fertility.infertility.cause.endometriosis",
    icon: Flame,
    sex: "AFAB",
  },
  {
    id: "premature_ovarian_insufficiency",
    labelKey:
      "med_history.fertility.infertility.cause.premature_ovarian_insufficiency",
    icon: Hourglass,
    sex: "AFAB",
  },
  // AMAB-relevant (sperm production / anatomy / function)
  {
    id: "production",
    labelKey: "med_history.fertility.infertility.cause.production",
    icon: Droplets,
    sex: "AMAB",
  },
  {
    id: "anatomical",
    labelKey: "med_history.fertility.infertility.cause.anatomical",
    icon: ScanHeart,
    sex: "AMAB",
  },
  {
    id: "functional",
    labelKey: "med_history.fertility.infertility.cause.functional",
    icon: Activity,
    sex: "AMAB",
  },
  // Both
  {
    id: "endocrine",
    labelKey: "med_history.fertility.infertility.cause.endocrine",
    icon: FlaskConical,
    sex: "both",
  },
  {
    id: "genetics",
    labelKey: "med_history.fertility.infertility.cause.genetics",
    icon: Dna,
    sex: "both",
  },
  {
    id: "psychosexual",
    labelKey: "med_history.fertility.infertility.cause.psychosexual",
    icon: BrainCircuit,
    sex: "both",
  },
  {
    id: "unexplained_infertility",
    labelKey: "med_history.fertility.infertility.cause.unexplained_infertility",
    icon: SearchX,
    sex: "both",
  },
  {
    id: "other",
    labelKey: "med_history.fertility.infertility.cause.other",
    icon: Plus,
    sex: "both",
  },
];

export function InfertilityCauseScreen() {
  const t = useTranslations();
  const { state, updateState } = useInfertility();
  const { data: patient } = usePatient();
  const patientSex = patient?.gender;
  const visibleCauses = useMemo(
    () =>
      causes.filter(
        (c) => c.sex === "both" || !patientSex || c.sex === patientSex,
      ),
    [patientSex],
  );
  const selected = useMemo<Set<CauseOption>>(
    () => new Set((state.causes as CauseOption[] | undefined) ?? []),
    [state.causes],
  );
  const navigation =
    useNavigation<NativeStackNavigationProp<InfertilityStackParamList>>();

  const toggleSelection = (id: CauseOption) => {
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
      // If unselecting "other", clear causeOther
      if (id === "other") {
        updateState({
          causes: Array.from(newSelected) as InfertilityCauseType[],
          causeOther: null,
        });
      } else {
        updateState({
          causes: Array.from(newSelected) as InfertilityCauseType[],
        });
      }
    } else {
      newSelected.add(id);
      updateState({
        causes: Array.from(newSelected) as InfertilityCauseType[],
      });
    }
  };

  const handleSave = () => {
    if (selected.has("other")) {
      navigation.navigate("InfertilityCauseOther");
    } else {
      navigation.navigate("PartnerInfertilityDiagnosis");
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.fertility.infertility.cause_title")}
      subtitle={t("med_history.fertility.infertility.cause_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={selected.size === 0}
      saveLabel={t("med_history.fertility.continue")}
    >
      <View style={styles.optionsGrid}>
        {visibleCauses.map((cause) => {
          const isSelected = selected.has(cause.id);
          return (
            <View key={cause.id} style={styles.optionWrapper}>
              <SquareOptionButton
                icon={cause.icon}
                label={t(cause.labelKey)}
                isSelected={isSelected}
                onPress={() => toggleSelection(cause.id)}
                iconSize={20}
                containerSize={40}
                labelSize="sm"
              />
            </View>
          );
        })}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  optionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
  optionWrapper: {
    width: "47%", // Approximately 2 columns with gap
    minWidth: "47%",
  },
});

export default InfertilityCauseScreen;
